import { Input, Label } from '@hqo/ui-atoms';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { CreateVisitFormV2Values } from '../../../types/form-data.types';
import { useValidationMessage } from '../../../hooks/use-validation-message.hook';

interface DateInputFieldProps {
  name: 'visitStartDate' | 'visitEndDate' | 'repeatEndDate';
  control: Control<CreateVisitFormV2Values>;
  errors: FieldErrors<CreateVisitFormV2Values>;
  label: string;
  id: string;
  getDateString: (date: Date | null | undefined) => string;
  parseDateString: (dateString: string | null | undefined) => Date;
  fallbackDate?: Date;
  helperText?: string;
  required?: boolean;
  fixedLabelHeight?: boolean;
}

export const DateInputField = ({
  name,
  control,
  errors,
  label,
  id,
  getDateString,
  parseDateString,
  fallbackDate,
  helperText,
  required = false,
  fixedLabelHeight = true,
}: DateInputFieldProps): JSX.Element => {
  const formatValidationMessage = useValidationMessage();
  const error = errors[name];

  return (
    <div className="flex flex-col">
      {/* Label area - fixed height to align inputs */}
      <div className={`mb-1.5 flex items-end ${fixedLabelHeight ? 'h-7' : ''}`}>
        <Label htmlFor={id} className="gap-1">
          {label}
          {required && <span className="text-destructive">*</span>}
        </Label>
      </div>
      <Controller
        name={name}
        control={control}
        // eslint-disable-next-line react/jsx-no-bind
        render={({ field }) => (
          <>
            <Input
              id={id}
              type="date"
              value={getDateString(field.value || fallbackDate)}
              // eslint-disable-next-line react/jsx-no-bind
              onChange={(e) => {
                const date = parseDateString(e.target.value);
                field.onChange(date);
              }}
              className={`w-full [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none ${
                error ? 'border-destructive' : ''
              }`}
              style={{
                // Hide native date picker in all browsers
                WebkitAppearance: 'none',
                MozAppearance: 'textfield',
              }}
            />
            {/* Error message or helper text area - fixed height */}
            <div className="mt-1 h-5">
              {error ? (
                <p className="text-sm text-destructive">{formatValidationMessage(error.message as string)}</p>
              ) : (
                helperText && <p className="text-xs text-muted-foreground">{helperText}</p>
              )}
            </div>
          </>
        )}
      />
    </div>
  );
};
